import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useProBranding, saveBranding, type ProBranding } from "@/hooks/use-pro-branding";
import { Loader2, Upload, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/marca")({
  head: () => ({
    meta: [
      { title: "Personalize sua marca nos PDFs do MEI | MEI Toolbox" },
      {
        name: "description",
        content:
          "Adicione logo, CNPJ, e-mail, telefone e endereço da sua empresa para personalizar todos os PDFs gerados nas ferramentas gratuitas para MEI do MEI Toolbox.",
      },
      { property: "og:title", content: "Personalize sua marca nos PDFs — MEI Toolbox" },
      {
        property: "og:description",
        content: "Configure logo e dados da sua empresa para personalizar os PDFs do MEI Toolbox.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/marca" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/marca" }],
  }),
  component: BrandPage,
});

const EMPTY_FORM = {
  company_name: "",
  cnpj: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  footer_note: "",
};

function BrandPage() {
  const { branding, loading } = useProBranding();
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!branding) return;
    setForm({
      company_name: branding.company_name ?? "",
      cnpj: branding.cnpj ?? "",
      email: branding.email ?? "",
      phone: branding.phone ?? "",
      website: branding.website ?? "",
      address: branding.address ?? "",
      footer_note: branding.footer_note ?? "",
    });
    setLogoUrl(branding.logo_url);
  }, [branding]);

  const update =
    (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      toast.error("Logo muito grande (máx 1MB para guardar no navegador)");
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Use PNG, JPG ou WEBP");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setLogoUrl(dataUrl);
      toast.success("Logo carregada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler o arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    toast.success("Logo removida");
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: ProBranding = {
        company_name: form.company_name || null,
        cnpj: form.cnpj || null,
        email: form.email || null,
        phone: form.phone || null,
        website: form.website || null,
        address: form.address || null,
        footer_note: form.footer_note || null,
        logo_url: logoUrl,
      };
      saveBranding(payload);
      toast.success("Marca salva! Seus PDFs já estão personalizados.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="container mx-auto max-w-3xl px-4 py-12">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
        ← Voltar para o início
      </Link>
      <h1 className="mt-3 text-3xl font-bold">Sua marca nos PDFs</h1>
      <p className="mt-2 text-muted-foreground">
        Estes dados aparecem no cabeçalho e rodapé de <strong>todos</strong> os PDFs que você gerar
        nas ferramentas (DAS, precificação, contratos, etc.). Tudo fica salvo só no seu navegador.
      </p>

      <form onSubmit={handleSave} className="mt-8 space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-bold flex items-center gap-2">
            <ImageIcon className="h-4 w-4" /> Logo
          </h2>
          <p className="text-sm text-muted-foreground mt-1">PNG, JPG ou WEBP, até 1MB.</p>
          <div className="mt-4 flex items-center gap-4">
            <div className="h-24 w-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Visualização do logotipo da sua empresa MEI nos PDFs"
                  title="Sua marca aplicada nos PDFs"
                  className="h-full w-full object-contain"
                />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-muted transition">
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {logoUrl ? "Trocar logo" : "Enviar logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </label>
              {logoUrl && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="inline-flex items-center gap-1.5 text-xs text-destructive hover:underline"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remover
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 grid md:grid-cols-2 gap-4">
          <Field
            label="Nome da empresa / razão social"
            value={form.company_name}
            onChange={update("company_name")}
            placeholder="João da Silva 12345678901"
          />
          <Field
            label="CNPJ"
            value={form.cnpj}
            onChange={update("cnpj")}
            placeholder="00.000.000/0000-00"
          />
          <Field
            label="E-mail"
            value={form.email}
            onChange={update("email")}
            placeholder="contato@suaempresa.com"
            type="email"
          />
          <Field
            label="Telefone / WhatsApp"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(11) 99999-9999"
          />
          <Field
            label="Site / Instagram"
            value={form.website}
            onChange={update("website")}
            placeholder="instagram.com/suaempresa"
          />
          <Field
            label="Endereço"
            value={form.address}
            onChange={update("address")}
            placeholder="Rua, número, cidade — UF"
          />
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold mb-1">Texto do rodapé (opcional)</label>
            <textarea
              value={form.footer_note}
              onChange={update("footer_note")}
              rows={2}
              placeholder="Ex: Obrigado pela preferência! • Pagamento via Pix: 12345..."
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar marca
        </button>
      </form>

      <article className="mt-16 prose prose-sm max-w-none dark:prose-invert">
        <h2>Personalize os PDFs do MEI com a marca da sua empresa</h2>
        <p>
          Adicionar logotipo, CNPJ, telefone e endereço aos documentos é um dos passos mais simples
          — e mais eficazes — para profissionalizar a comunicação do seu negócio. Para o cliente, um
          recibo ou contrato com a sua marca transmite mais confiança do que um arquivo genérico.
          Para você, é a chance de reforçar a identidade da sua empresa em cada interação, sem
          precisar contratar designer ou comprar um editor de PDF.
        </p>

        <h3>Onde a marca aparece</h3>
        <p>
          Os dados salvos nesta página são aplicados automaticamente em <strong>todos</strong> os
          PDFs gerados nas <Link to="/ferramentas">ferramentas do MEI Toolbox</Link>: contratos de
          prestação de serviço, recibos, relatórios de DAS, simulações de empréstimo, planilhas de
          precificação e qualquer outro documento exportável. O logotipo entra no cabeçalho, os
          dados de contato no rodapé e o texto opcional aparece como nota final — perfeito para
          mensagens como "Pagamento via Pix: chave@exemplo.com" ou "Obrigado pela preferência".
        </p>

        <h3>O que preencher (e por quê)</h3>
        <ul>
          <li>
            <strong>Logo (PNG, JPG ou WEBP até 1MB):</strong> use preferencialmente uma imagem com
            fundo transparente para que ela combine com qualquer cor de cabeçalho.
          </li>
          <li>
            <strong>Nome / razão social:</strong> aparece em destaque junto ao logotipo.
          </li>
          <li>
            <strong>CNPJ:</strong> obrigatório para contratos e recibos com valor fiscal.
          </li>
          <li>
            <strong>E-mail e telefone:</strong> facilita que o cliente entre em contato após receber
            o documento.
          </li>
          <li>
            <strong>Site ou Instagram:</strong> ótimo para divulgar canais de venda em recibos.
          </li>
          <li>
            <strong>Endereço:</strong> exigido em contratos de prestação de serviço e algumas notas
            fiscais.
          </li>
          <li>
            <strong>Rodapé opcional:</strong> use para chave Pix, política de devolução ou
            agradecimento.
          </li>
        </ul>

        <h3>Privacidade: nada sai do seu navegador</h3>
        <p>
          Toda a configuração de marca — incluindo a logo — é salva exclusivamente no storage local
          do navegador. O MEI Toolbox não armazena seus dados em servidores, não compartilha com
          terceiros e não exige cadastro. Isso significa que limpar os dados de navegação ou trocar
          de aparelho fará com que você precise refazer a configuração. Para guardar uma cópia,
          basta fazer download de um PDF qualquer depois de salvar a marca — o arquivo terá tudo
          aplicado.
        </p>

        <h3>Dicas para um resultado profissional</h3>
        <p>
          Use uma logo quadrada ou levemente retangular (entre 200x200 e 600x600 pixels) para que
          não fique esticada. Prefira o formato WEBP ou PNG transparente quando possível. Mantenha o
          texto do rodapé curto — duas linhas no máximo — para não competir com o conteúdo do
          documento. Depois de salvar, gere um contrato de teste em{" "}
          <Link to="/ferramentas/contratos-pdf">Gerador de contratos</Link>
          para conferir o resultado antes de enviar a um cliente real.
        </p>
      </article>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (e: any) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
